import { useEffect } from 'react';

/**
 * Hook to warn users about unsaved changes when navigating away from the page
 * 
 * Attaches a beforeunload event listener that shows a browser confirmation dialog
 * when the user attempts to:
 * - Close the browser tab/window
 * - Navigate to a different URL
 * - Refresh the page
 * 
 * Note: Modern browsers ignore custom messages and show a standard warning.
 * 
 * @param hasUnsavedChanges - Whether there are unsaved changes
 * @param message - Optional custom warning message (ignored by modern browsers)
 * 
 * @example
 * ```tsx
 * const form = useProviderEditorState();
 * useUnsavedChanges(form.formState.isDirty);
 * ```
 */
export function useUnsavedChanges(
  hasUnsavedChanges: boolean,
  message = 'You have unsaved changes. Are you sure you want to leave?'
): void {
  useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent): string => {
      // Cancel the event to show confirmation dialog
      event.preventDefault();
      
      // Modern browsers require returnValue to be set
      // Chrome requires returnValue to be set
      event.returnValue = message;
      
      // Some older browsers use the return value
      return message;
    };

    // Attach the event listener
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup: Remove event listener when component unmounts or hasUnsavedChanges becomes false
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, message]);
}

